import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import InvestorPortalLayout from '../../../components/market/InvestorPortalLayout';
import { useInvestorAuth } from '../../../context/InvestorAuthContext';
import { Icon } from '../../../utils/icons';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Doorly brand palette only — primary blue #3a6ad9 for informational
// alerts, lime #acf44e (rendered against light tint) for success-class
// access events, near-black #111111 for neutral notifications.
const TYPE_CFG = {
  pool_available:  { iconName: 'chart',        label: 'Pool Available',  color: '#3a6ad9', bg: '#eff3fe' },
  bid_update:      { iconName: 'list',         label: 'Bid Update',      color: '#3a6ad9', bg: '#eff3fe' },
  document_ready:  { iconName: 'document',     label: 'Document Ready',  color: '#111111', bg: '#f3f4f6' },
  message:         { iconName: 'chat',         label: 'Message',         color: '#3a6ad9', bg: '#eff3fe' },
  access_granted:  { iconName: 'check-circle', label: 'Access Granted',  color: '#111111', bg: '#f0fde4' },
  general:         { iconName: 'bell',         label: 'Notification',    color: '#111111', bg: '#f3f4f6' },
};

const timeAgo = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), dy = Math.floor(diff / 86400000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (dy === 1) return 'Yesterday';
  if (dy < 7) return `${dy}d ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function InvestorAlerts() {
  const { token } = useInvestorAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/market/investor/alerts`, { headers });
      setAlerts(res.data?.alerts || []);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const markRead = async (id) => {
    setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, is_read: true } : a));
    await axios.patch(`${API_URL}/api/market/investor/alerts/${id}/read`, {}, { headers }).catch(() => {});
  };

  const markAllRead = async () => {
    setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })));
    await axios.patch(`${API_URL}/api/market/investor/alerts/read-all`, {}, { headers }).catch(() => {});
  };

  const handleClick = (alert) => {
    if (!alert.is_read) markRead(alert.id);
    if (alert.link_url) {
      if (alert.link_url.startsWith('http')) {
        window.open(alert.link_url, '_blank');
      } else {
        navigate(alert.link_url);
      }
    }
  };

  const unread = alerts.filter((a) => !a.is_read).length;
  const filtered = filter === 'unread' ? alerts.filter((a) => !a.is_read) : alerts;

  return (
    <InvestorPortalLayout>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111111' }}>Alerts</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              {unread > 0 ? `${unread} unread · ${alerts.length} total` : 'All caught up'}
            </p>
          </div>
          {unread > 0 && (
            <button onClick={markAllRead}
              style={{ padding: '8px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
              Mark all read
            </button>
          )}
        </div>

        {alerts.length > 0 && (
          <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#f3f4f6', borderRadius: 10, padding: 4, width: 'fit-content' }}>
            {[{ value: 'all', label: `All (${alerts.length})` }, { value: 'unread', label: `Unread (${unread})` }].map((tab) => (
              <button key={tab.value} onClick={() => setFilter(tab.value)}
                style={{ padding: '7px 16px', background: filter === tab.value ? '#fff' : 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: filter === tab.value ? 700 : 500, color: filter === tab.value ? '#111111' : '#6b7280', boxShadow: filter === tab.value ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 }}>
            Loading alerts…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 60, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 14px', borderRadius: 12, background: '#eff3fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="bell" size={26} color="#3a6ad9" />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
              {filter === 'unread' ? 'No unread alerts' : 'No alerts at this time.'}
            </div>
            <div style={{ fontSize: 13, color: '#9ca3af' }}>
              {filter === 'unread' ? "You're all caught up." : "We'll notify you when there's something new."}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map((alert) => {
              const cfg = TYPE_CFG[alert.alert_type] || TYPE_CFG.general;
              const isRead = !!alert.is_read;
              return (
                <div key={alert.id} onClick={() => handleClick(alert)}
                  style={{
                    background: isRead ? '#fff' : '#fafcff',
                    border: `1px solid ${isRead ? '#e5e7eb' : '#bfdbfe'}`,
                    borderLeft: `4px solid ${isRead ? '#e5e7eb' : cfg.color}`,
                    borderRadius: 12, padding: '14px 16px',
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                    cursor: 'pointer', transition: 'background 0.12s',
                  }}
                  onMouseEnter={(e) => { if (!isRead) e.currentTarget.style.background = '#f0f4ff'; }}
                  onMouseLeave={(e) => (e.currentTarget.style.background = isRead ? '#fff' : '#fafcff')}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name={cfg.iconName} size={18} color={cfg.color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                      <div style={{ fontSize: 14, fontWeight: isRead ? 600 : 700, color: '#111111', lineHeight: 1.4 }}>{alert.title}</div>
                      {!isRead && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3a6ad9', flexShrink: 0, marginTop: 4 }} />}
                    </div>
                    {alert.body && (
                      <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5, marginBottom: 6 }}>{alert.body}</div>
                    )}
                    <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{cfg.label}</span>
                      <span>·</span>
                      <span>{timeAgo(alert.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </InvestorPortalLayout>
  );
}
